<script setup>
import { CardsStats } from "@easy/ui/cards";
import { computed, onMounted, ref, watch } from "vue";
import { playerStatsService } from "@/apps/player/services/player-stats.service";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { FilterList } from "@easy/ui/filters";
import { i18n } from "@/i18n";
import { statFactory } from "@/apps/player/panel/stat-factory";
import { DATE_RANGE_MAPPER, DATE_RANGE_TYPE } from "@/apps/player/panel/lessons/date-range";
import { useFilterList } from "@/shared/utils/useFilterList";

const { t } = useI18n();
const { params } = useRoute();
const playerUUID = computed(() => params.projectUuid);
const stats = ref([]);
const lang = ref(i18n.global.locale.value);

const pageSize = defineModel("pageSize", { required: true });
const dateRange = defineModel("dateRange", { required: true });

const filtersData = ref([
    {
        name: "calendar",
        label: t("shared.filters.dates"),
        value: DATE_RANGE_TYPE.all,
        options: [
            { label: t("shared.filters.date.custom"), value: DATE_RANGE_TYPE.custom },
            { label: t("shared.filters.date.all"), value: DATE_RANGE_TYPE.all },
            { label: t("shared.filters.date.today"), value: DATE_RANGE_TYPE.today },
            { label: t("shared.filters.date.last7days"), value: DATE_RANGE_TYPE.last7days },
            { label: t("shared.filters.date.last14days"), value: DATE_RANGE_TYPE.last14days },
            { label: t("shared.filters.date.last30days"), value: DATE_RANGE_TYPE.last30days },
            { label: t("shared.filters.date.last3months"), value: DATE_RANGE_TYPE.last3months },
            { label: t("shared.filters.date.last12months"), value: DATE_RANGE_TYPE.last12months },
        ],
        type: "calendar",
        startDate: new Date(),
        endDate: new Date(),
    },
    {
        name: "perPage",
        label: t("shared.filters.per_page"),
        value: pageSize,
        options: [
            { label: "10", value: 10 },
            { label: "20", value: 20 },
            { label: "50", value: 50 },
            { label: "100", value: 100 },
        ],
    },
]);

const { applyFilter, applyCustomFilter } = useFilterList(filtersData, {
    calendar(value, type = "") {
        const { from, to, queryFor } =
            type === "custom" && Array.isArray(value)
                ? DATE_RANGE_MAPPER.custom(value)
                : DATE_RANGE_MAPPER[value]?.() || {};
        if (queryFor) {
            dateRange.value = queryFor;
            return { startDate: new Date(from), endDate: new Date(to) };
        }
        return {};
    },
});

onMounted(() => loadGeneralStats());
watch([playerUUID, dateRange], () => {
    loadGeneralStats();
});

async function loadGeneralStats() {
    try {
        stats.value = [];
        const generalStats = await playerStatsService.getSummaryForStudents(playerUUID.value, {
            dateRange: dateRange.value,
        });
        const { hasNoPreviousRange, numberOfStudents, numberOfVisits, averageLessonProgress } =
            generalStats || {};
        stats.value = [
            statFactory(t("player.panel.stats.students"), numberOfStudents, { hasNoPreviousRange }),
            statFactory(t("player.panel.stats.visits"), numberOfVisits, { hasNoPreviousRange }),
            statFactory(t("player.panel.stats.progress"), averageLessonProgress, {
                hasNoPreviousRange,
                decorateWith: "%",
                calculateChange(current, previous) {
                    // difference in percent points.
                    return {
                        change: current - previous,
                        changeSign: " p.p.",
                    };
                },
            }),
        ];
    } catch (e) {
        console.error(e);
    }
}
</script>

<template>
    <div class="flex flex-col">
        <div
            class="order-1 lg:order-0 flex items-center lg:py-3 lg:px-6 lg:space-x-6 border-b ct-border-2"
        >
            <div class="flex-grow">
                <FilterList
                    :filters="filtersData"
                    :lang="lang"
                    :label="$t('shared.filters.title')"
                    @select="applyFilter"
                    @selectCustom="applyCustomFilter"
                />
            </div>
        </div>
        <CardsStats :stats="stats" class="order-0 lg:order-1 border-b ct-border-2" />
    </div>
</template>
